import type { KeyboardEvent } from 'react';

import { cn } from '@poseidon/components/cn';
import { isNotEmptyTipTap } from '@poseidon/components/utils';

import { stepEditSpec, type SequenceStep } from '~/components/Sequence/sequenceSteps';

/**
 * SSR-safe, unicode-preserving plain-text from a step's TipTap HTML. We deliberately DON'T reuse the
 * shared `getTextFromHtml` here: it runs `DOMParser` (browser-only, so it can't run during the page's
 * server render) and strips every non-ASCII char (`[^\x20-\x7E]`), which would mangle names/emoji in a
 * preview. This regex strip keeps unicode and works on both the server and client passes.
 */
function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Cap the raw string so an enormous body doesn't bloat the DOM — the line-clamp then trims what shows. */
function fieldText(html: string | undefined): string {
  if (!isNotEmptyTipTap(html)) return '';
  const text = htmlToText(html ?? '');
  return text.length > 240 ? `${text.slice(0, 240).trimEnd()}…` : text;
}

/**
 * Whether a step has authored content worth previewing beside it — an editable content step (message /
 * connect note / inmail / email / call note / reminder note) with a non-empty subject or body. `like`
 * (a count, no text) and the non-content steps (follow / wait / await / containers) have nothing to show.
 * The single source of truth the graph uses to decide whether to render a preview + reserve room for it.
 */
export function stepHasContentPreview(step: SequenceStep): boolean {
  return stepEditSpec(step.type) != null && (isNotEmptyTipTap(step.subject) || isNotEmptyTipTap(step.body));
}

interface Props {
  step: SequenceStep;
  /**
   * Open this step's content editor (the card is a large click target for editing, like clicking the node).
   * Omit → a STATIC, non-interactive preview (the locked per-contact graph passes this only for editable,
   * not-yet-sent steps, so a sent step's preview isn't a dead "Edit step content" click target).
   */
  onEdit?: () => void;
  /** Cap the card width (px) when branch lanes claim the right gutter, so the preview never overlaps them. */
  maxWidthPx?: number;
}

/**
 * A compact, read-at-a-glance preview of a content step's authored text, shown in the gutter to the RIGHT
 * of its node on the sequence graph (the people-piles sit on the left). Email / InMail lead with the bold
 * subject; every content step shows a body snippet. Clicking the card opens that step's editor (when
 * `onEdit` is provided; otherwise it's static text). Renders nothing when the step has no content — see
 * {@link stepHasContentPreview}.
 */
export function SequenceStepPreview({ step, onEdit, maxWidthPx }: Props) {
  const subject = fieldText(step.subject);
  const body = fieldText(step.body);
  if (subject === '' && body === '') return null;

  const content = (
    <>
      {subject !== '' && <div className="line-clamp-1 font-medium">{subject}</div>}
      {body !== '' && <div className={cn('leading-snug', subject === '' ? 'line-clamp-3' : 'line-clamp-2')}>{body}</div>}
    </>
  );

  // Static (non-editable) preview — plain faint text, no button affordance.
  if (onEdit == null) {
    return (
      <div style={{ maxWidth: maxWidthPx }} className="w-56 text-left text-sm text-lynch-400 dark:text-lynch-500">
        {content}
      </div>
    );
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onEdit();
    }
  };

  // No card / border / background — the text sits faint directly on the canvas and darkens on hover; the
  // node itself is the visual anchor. Color is inherited from the wrapper so hover moves the whole block.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={onKeyDown}
      aria-label="Edit step content"
      style={{ maxWidth: maxWidthPx }}
      className={cn(
        'w-56 cursor-pointer text-left text-sm text-lynch-400 transition-colors',
        'hover:text-lynch-600 focus:outline-none focus-visible:text-lynch-600',
        'dark:text-lynch-500 dark:hover:text-lynch-300',
      )}>
      {content}
    </div>
  );
}
